import React, { useRef, useState } from "react";
import "./modal_list_item_location.css";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, message, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import axios from 'axios';

interface DataType {
    key: React.Key;
    id: number;
    code_rack: string;
    name_rack: string;
    status: string;
    item_code?: string;
    warehouse_code: string;
}

interface ModalListItemLocationProps {
    onRowSelect: (record: DataType) => void;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const ModalListItemLocation: React.FC<ModalListItemLocationProps> = ({ onRowSelect }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const { data, error } = useSWR<DataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/rack-locations`, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            String(record[dataIndex] ?? '')
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Code Location',
            dataIndex: 'code_rack',
            key: 'code_rack',
            sorter: (a, b) => a.code_rack.localeCompare(b.code_rack),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('code_rack'),
        },
        {
            title: 'Name Location',
            dataIndex: 'name_rack',
            key: 'name_rack',
            sorter: (a, b) => a.name_rack.localeCompare(b.name_rack),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('name_rack'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('status'),
        },
    ];

    if (error) return <div>Failed to load data.</div>;

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="iaa4">
                <div className="gjs-grid-column" id="iwiw">
                    <Table<DataType>
                        columns={columns}
                        dataSource={data}
                        loading={!data}
                        onRow={(record) => ({
                            onClick: () => {
                                if (record.item_code) {
                                    message.warning(`Item Part sudah ada di tempati oleh (${record.item_code}) tersebut`);
                                } else {
                                    onRowSelect(record);
                                }
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalListItemLocation;